import React, { useCallback, useEffect } from 'react';
import { Info, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';

import { PermissionSlug } from '../../constants/featureFlags';
import { PAYMENT_OASIS_EXTERNAL_MESSAGE } from '../../constants/billing';
import { useStore } from '../../store';
import {
  billingOrderPath,
  patientAuthorizationsPath,
  patientEpisodePath,
  patientInfoPath,
  patientVisitPath,
} from '../../utils/navigation';
import { getStatusLabel } from '../task/taskStatus';
import type {
  BillingReadinessCategory,
  BillingReadinessReason,
  BillingReadinessTask,
  GetBillingCyclesResponse,
} from '../../services/api/types.gen';
import {
  useClaimReadiness,
  type DraftReadinessScope,
} from './ClaimReadinessProvider';
import { getClaimReadinessCategories } from './ClaimReadinessCell';

const CorrectionLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <Link
    to={to}
    className='font-medium text-primary underline-offset-2 hover:underline'
  >
    {children}
  </Link>
);

const PASS_CONFIRMATION: Partial<Record<BillingReadinessCategory, string>> = {
  oasis: 'The payment OASIS is available, signed and exported.',
  hipps: 'Payment OASIS and valid diagnoses are available for HIPPS.',
  visits: 'All billable visits are in a billing-ready status.',
  orders: 'All billing orders are satisfied.',
};

export const EMPTY_EVIDENCE_TEXT =
  'Details unavailable. Refresh claims to check the latest state.';
export const LOADING_TEXT = 'Loading readiness details…';
export const ERROR_TEXT = 'Unable to load readiness details. Try again.';
export const UNAVAILABLE_SCOPE_TEXT =
  'Readiness details are not available for the current list.';

/**
 * The provider-evidence side of a readiness category.
 *
 * A failing category reads its reasons from the parent-owned readiness-details
 * read; a passing category uses the row's own flags and makes no request. The
 * correction links reuse the same destinations the full preview uses, subject
 * to the same permissions, and are plain router links, so ordinary,
 * Cmd-click and Ctrl-click navigation behave as everywhere else.
 */
export const ClaimReadinessDetails: React.FC<{
  entry: GetBillingCyclesResponse;
  scope: DraftReadinessScope;
  category?: BillingReadinessCategory;
  fieldOnly?: boolean;
}> = ({ entry, scope, category, fieldOnly = false }) => {
  const { revision, getEntry, isScopeAdmitted, loadDetails } =
    useClaimReadiness();
  const { canViewOrders, canEditPatientInfo } = useStore(
    useShallow(state => ({
      canViewOrders: state.hasPermission(PermissionSlug.ORDER_MANAGER),
      canEditPatientInfo: state.hasPermission(PermissionSlug.PATIENT_INFO_EDIT),
    }))
  );

  const cycleId = entry.billing_cycle_id;
  const states = getClaimReadinessCategories(entry);
  const detail = getEntry(scope, cycleId);
  const scopeAdmitted = isScopeAdmitted(scope, cycleId);
  const fieldCodes = fieldFailureCodes(entry);
  const needsEvidence = fieldOnly
    ? fieldCodes.size > 0
    : category != null &&
      states.find(item => item.category === category)?.state === 'fail';

  useEffect(() => {
    if (!needsEvidence) return;
    // Nothing to request until the caller's current scope admits this cycle; a
    // later provider notification (registration) re-runs this effect.
    if (!isScopeAdmitted(scope, cycleId)) return;
    if (getEntry(scope, cycleId)) return;
    loadDetails(scope, cycleId);
  }, [
    needsEvidence,
    scope,
    cycleId,
    revision,
    getEntry,
    isScopeAdmitted,
    loadDetails,
  ]);

  const retry = useCallback(() => {
    void loadDetails(scope, cycleId, true);
  }, [loadDetails, scope, cycleId]);

  // Informational reasons (e.g. diagnosis codes that could not be checked)
  // are context, not failures: they never count toward a category's verdict
  // and are listed apart from the blocking reasons.
  const successReasons = (opts?: {
    category?: BillingReadinessCategory;
    codes?: Set<BillingReadinessReason['code']>;
    informational?: boolean;
  }): BillingReadinessReason[] => {
    if (!detail || detail.status !== 'success' || !detail.data) return [];
    const informational = opts?.informational ?? false;
    return detail.data.reasons.filter(reason => {
      if ((reason.informational ?? false) !== informational) return false;
      if (opts?.category && reason.category !== opts.category) return false;
      if (opts?.codes && !opts.codes.has(reason.code)) return false;
      return true;
    });
  };

  const renderInformational = (category: BillingReadinessCategory) => {
    const notes = successReasons({ category, informational: true });
    if (notes.length === 0) return null;
    return (
      <ul className='space-y-1'>
        {notes.map((note, index) => (
          <li
            key={`${note.code}-${index}`}
            className='flex items-start gap-1.5 text-muted-foreground'
          >
            <Info aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            <span>{note.message}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderTaskStatus = (task: BillingReadinessTask) => (
    <span className='ml-1 text-muted-foreground'>
      {getStatusLabel(task.status)}
    </span>
  );

  const renderTask = (task: BillingReadinessTask) => {
    const label =
      task.kind === 'order' && !canViewOrders ? (
        <span className='font-medium text-foreground'>{task.name}</span>
      ) : (
        <CorrectionLink
          to={
            task.kind === 'order'
              ? billingOrderPath({
                  orderId: task.task_id,
                  status: task.status,
                  patientId: entry.patient_id,
                  episodeId: entry.episode_id,
                })
              : patientVisitPath(
                  entry.patient_id,
                  task.task_id,
                  task.episode_id ?? entry.episode_id
                )
          }
        >
          {task.name}
        </CorrectionLink>
      );
    return (
      <span>
        {label}
        {renderTaskStatus(task)}
      </span>
    );
  };

  const renderTasks = (tasks: BillingReadinessTask[]) =>
    tasks.length > 0 ? (
      <ul className='mt-1 space-y-0.5'>
        {tasks.map(task => (
          <li key={task.task_id}>{renderTask(task)}</li>
        ))}
      </ul>
    ) : null;

  const correctionLink = (reason: BillingReadinessReason) => {
    switch (reason.code) {
      case 'payment_oasis':
      case 'hipps_payment_oasis':
        return (
          <CorrectionLink
            to={patientEpisodePath(entry.patient_id, entry.episode_id)}
          >
            Open episode visits
          </CorrectionLink>
        );
      case 'no_billable_visits':
        return (
          <CorrectionLink
            to={patientEpisodePath(entry.patient_id, entry.episode_id)}
          >
            Review billing period visits
          </CorrectionLink>
        );
      case 'mbi':
        return canEditPatientInfo ? (
          <CorrectionLink
            to={patientInfoPath(entry.patient_id, entry.episode_id)}
          >
            Patient Info → MBI
          </CorrectionLink>
        ) : null;
      case 'pcr': {
        const field =
          detail?.status === 'success' && detail.data
            ? detail.data.pcr_field === 'pcr_number_1'
              ? 'PCR 1'
              : 'PCR 2'
            : null;
        return (
          <CorrectionLink
            to={patientEpisodePath(entry.patient_id, entry.episode_id)}
          >
            Edit episode → Pre-Claim Review{field ? ` (${field})` : ''}
          </CorrectionLink>
        );
      }
      case 'authorization':
        return (
          <CorrectionLink
            to={patientAuthorizationsPath(entry.patient_id, entry.episode_id)}
          >
            Open authorizations
          </CorrectionLink>
        );
      default:
        return null;
    }
  };

  const renderReason = (reason: BillingReadinessReason, index: number) => (
    <li key={`${reason.code}-${index}`} className='space-y-0.5'>
      <p className='text-foreground'>{reason.message}</p>
      {renderTasks(reason.tasks ?? [])}
      {correctionLink(reason)}
    </li>
  );

  const renderLoading = () => (
    <div className='flex items-center gap-2 text-muted-foreground'>
      <Loader2 aria-hidden='true' className='h-3.5 w-3.5 animate-spin' />
      {LOADING_TEXT}
    </div>
  );

  const renderError = () => (
    <div className='flex flex-col gap-2'>
      <p className='text-red-800' role='alert'>
        {ERROR_TEXT}
      </p>
      <Button
        type='button'
        variant='link'
        size='sm'
        className='h-auto justify-start p-0 text-xs'
        onClick={retry}
      >
        <RefreshCw aria-hidden='true' className='mr-1 h-3 w-3' />
        Retry
      </Button>
    </div>
  );

  const renderUnavailableScope = () => (
    <p className='text-muted-foreground'>{UNAVAILABLE_SCOPE_TEXT}</p>
  );

  const renderEvidence = (opts?: {
    category?: BillingReadinessCategory;
    codes?: Set<BillingReadinessReason['code']>;
  }): React.ReactNode => {
    if (!detail) {
      // An absent entry is only a pending request when the caller's scope
      // admits one; an unregistered scope, a placeholder list or a cycle that
      // left membership can never load, so it shows unavailable evidence.
      return scopeAdmitted ? renderLoading() : renderUnavailableScope();
    }
    if (detail.status === 'loading') return renderLoading();
    if (detail.status === 'error') return renderError();
    const reasons = successReasons(opts);
    const notes = opts?.category ? renderInformational(opts.category) : null;
    return (
      <div className='space-y-2'>
        {reasons.length === 0 ? (
          <p className='text-muted-foreground'>{EMPTY_EVIDENCE_TEXT}</p>
        ) : (
          <ul className='space-y-2'>{reasons.map(renderReason)}</ul>
        )}
        {notes}
      </div>
    );
  };

  const childRow = ({
    label,
    required,
    valid,
    passText,
    content,
  }: {
    label: string;
    required: boolean;
    valid: boolean;
    passText: string;
    content?: React.ReactNode;
  }) => (
    <li className='space-y-0.5'>
      <div className='flex items-center justify-between gap-2'>
        <span className='font-medium text-foreground'>{label}</span>
        <span className='text-muted-foreground'>
          {!required ? 'Not required' : valid ? passText : 'Not satisfied'}
        </span>
      </div>
      {required && !valid ? <ul className='space-y-0.5'>{content}</ul> : null}
    </li>
  );

  const renderInsurance = (failed: boolean) => {
    let reasons: BillingReadinessReason[] = [];
    if (failed) {
      if (!detail) {
        return scopeAdmitted ? renderLoading() : renderUnavailableScope();
      }
      if (detail.status === 'loading') return renderLoading();
      if (detail.status === 'error') return renderError();
      reasons = successReasons({
        category: 'insurance',
        codes: new Set<BillingReadinessReason['code']>([
          'mbi',
          'pcr',
          'authorization',
        ]),
      });
      if (reasons.length === 0) {
        return <p className='text-muted-foreground'>{EMPTY_EVIDENCE_TEXT}</p>;
      }
    }
    return (
      <ul className='space-y-2'>
        {childRow({
          label: 'MBI',
          required: entry.is_mbi_required,
          valid: entry.is_mbi_valid,
          passText: 'On file',
          content: reasons
            .filter(reason => reason.code === 'mbi')
            .map(renderReason),
        })}
        {childRow({
          label: 'PCR',
          required: entry.is_pcr_tracking_number_required,
          valid: entry.is_pcr_tracking_number_valid,
          passText: 'On file',
          content: reasons
            .filter(reason => reason.code === 'pcr')
            .map(renderReason),
        })}
        {childRow({
          label: 'Authorization',
          required: entry.is_authorization_required,
          valid: entry.is_authorization_valid,
          passText: 'On file',
          content: reasons
            .filter(reason => reason.code === 'authorization')
            .map(renderReason),
        })}
      </ul>
    );
  };

  if (category === 'insurance') {
    return renderInsurance(
      states.find(item => item.category === category)?.state === 'fail'
    );
  }

  if (category) {
    const state = states.find(item => item.category === category)?.state;
    if (state === 'pass') {
      // An external payment OASIS passes the gate but was neither signed nor
      // exported in Roger, so it must not use the local pass confirmation.
      const text =
        category === 'oasis' && entry.payment_oasis_status === 'external'
          ? `${PAYMENT_OASIS_EXTERNAL_MESSAGE}.`
          : PASS_CONFIRMATION[category];
      // A passing category makes no request, but details another category
      // already loaded may carry context for it.
      const notes = renderInformational(category);
      return notes ? (
        <div className='space-y-2'>
          <p className='text-foreground'>{text}</p>
          {notes}
        </div>
      ) : (
        <p className='text-foreground'>{text}</p>
      );
    }
    return renderEvidence({ category });
  }

  // Field-only mode: the draft full preview's small correction area, which
  // shows the field and missing-record links the preview sections lack.
  if (!needsEvidence) return null;
  return (
    <div className='space-y-2'>
      <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        Readiness corrections
      </p>
      {renderEvidence({ codes: fieldCodes })}
    </div>
  );
};

/**
 * The field / missing-record failures this cycle's own flags report, as reason
 * codes. They map 1:1 onto the readiness-details read's codes.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported for unit testing
export function fieldFailureCodes(
  entry: GetBillingCyclesResponse
): Set<BillingReadinessReason['code']> {
  const codes = new Set<BillingReadinessReason['code']>();
  // EMR-778: 'external' is a selection the claim rule accepted (history the
  // previous EMR billed); it is not a failure and must not request readiness
  // details that cannot exist for it.
  if (
    entry.payment_oasis_status !== 'eligible' &&
    entry.payment_oasis_status !== 'external'
  ) {
    codes.add('payment_oasis');
    codes.add('hipps_payment_oasis');
  }
  if (!entry.has_computable_hipps_inputs) codes.add('hipps_inputs');
  if (!entry.has_billable_visits) codes.add('no_billable_visits');
  if (entry.is_mbi_required && !entry.is_mbi_valid) codes.add('mbi');
  if (
    entry.is_pcr_tracking_number_required &&
    !entry.is_pcr_tracking_number_valid
  ) {
    codes.add('pcr');
  }
  if (
    entry.is_authorization_required === true &&
    !entry.is_authorization_valid
  ) {
    codes.add('authorization');
  }
  return codes;
}
